import { ActionIconDef, GridConfig, GridRow, ToolbarActionDef } from '@inavista/components/grid';

// Out of Office Requests on @inavista/components (shared library, routed in RIMS Sales and RIMS Service). Rows: RIMS Master
// api/grid/vacation-requests (sls.spGetAllVacationReq's rows for the caller in the token, only for a role with
// menuItem_OutOfOfficeRequest). A few hundred rows, so the grid loads them once and sorts, filters and searches in the
// browser. Same columns, labels, default visibility, status colours, row actions and toolbar as the Syncfusion grid it
// replaces. Approve / Reject / Delete still call the request endpoints they called before.

export const CREATE_REQUEST_ACTION = 'create-request';

const BLACK = '#000000de';
const GREEN = '#28a745';
const RED = '#dc3545';
const INACTIVE = '#cccccc';
const NO_PERMISSION = 'Your role does not allow this';

const SHOWN = [
  'vrfId', 'reporter', 'startDate', 'endDate', 'status', 'totalDays', 'vacation', 'floating', 'sick', 'other', 'createdDate',
  'approvedDate', 'approvedBy',
];
// The query's other columns: the action rules and colours read them, the old grid never showed them.
const BACKING_COLUMNS = [
  'userId', 'statusColor', 'comment', 'canApprove', 'canReject', 'canDelete', 'commentCount', 'attachmentCount',
  'linkColorCode', 'commentColor', 'attColorCode', 'isDeleted',
];
const DAYS = ['totalDays', 'vacation', 'floating', 'sick', 'other'];
const DATES = ['startDate', 'endDate', 'createdDate', 'approvedDate'];

const ICON = {
  approve: '<polyline points="20 6 9 17 4 12" />',
  reject: '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />',
  details: '<line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><polyline points="3 6 4 7 6 5" /><polyline points="3 12 4 13 6 11" /><polyline points="3 18 4 19 6 17" />',
  comments: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />',
  attachments: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><polyline points="3 3 3 8 8 8" /><polyline points="12 7 12 12 15 14" />',
  delete: '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />',
  add: '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />',
};

export interface Permission { visible: boolean; enable: boolean }

export interface VacationRequestsGridContext {
  masterApiUrl: string;
  canExport: boolean;
  approve: Permission;
  reject: Permission;
  delete: Permission;
  create: Permission;
}

// Why Approve / Reject / Delete is off for this row, or null when it is on. The rule is the row's (canApprove / canReject /
// canDelete from the request list's procedure); a role whose rule is greyed out never gets it.
export function approveDisabledReason(row: GridRow, ctx: VacationRequestsGridContext): string | null {
  if (!ctx.approve.enable) return `Approve: ${NO_PERMISSION}`;
  return row['canApprove'] ? null : 'Only submitted requests in your area can be approved.';
}
export function rejectDisabledReason(row: GridRow, ctx: VacationRequestsGridContext): string | null {
  if (!ctx.reject.enable) return `Reject: ${NO_PERMISSION}`;
  return row['canReject'] ? null : 'Only submitted or approved requests in your area can be rejected.';
}
export function deleteDisabledReason(row: GridRow): string | null {
  return row['canDelete'] ? null : 'Only your own open (not submitted) request can be deleted.';
}

// The old command column, in its order: Approve and Reject (with their rules), Details, Comments (orange with comments),
// Attachments (green with attachments), History, Delete (with its rule).
export function vacationRequestsActionIcons(ctx: VacationRequestsGridContext): ActionIconDef[] {
  const icons: ActionIconDef[] = [];
  if (ctx.approve.visible) icons.push({
    key: 'approve', label: 'Approve Request', icon: ICON.approve,
    colorFn: row => (approveDisabledReason(row, ctx) ? INACTIVE : GREEN),
    disabledFn: row => !!approveDisabledReason(row, ctx),
    labelFn: row => approveDisabledReason(row, ctx) ?? 'Approve Request',
  });
  if (ctx.reject.visible) icons.push({
    key: 'reject', label: 'Reject Request', icon: ICON.reject,
    colorFn: row => (rejectDisabledReason(row, ctx) ? INACTIVE : RED),
    disabledFn: row => !!rejectDisabledReason(row, ctx),
    labelFn: row => rejectDisabledReason(row, ctx) ?? 'Reject Request',
  });
  icons.push(
    { key: 'details', label: 'Details', icon: ICON.details, colorFn: () => BLACK },
    { key: 'comments', label: 'Comments', icon: ICON.comments, colorFn: row => String(row['commentColor'] || BLACK) },
    { key: 'attachments', label: 'Attachments', icon: ICON.attachments, colorFn: row => String(row['attColorCode'] || BLACK) },
    { key: 'history', label: 'History', icon: ICON.history, colorFn: () => BLACK },
  );
  if (ctx.delete.visible) icons.push({
    key: 'delete', label: 'Delete Request', icon: ICON.delete,
    colorFn: row => (deleteDisabledReason(row) ? INACTIVE : BLACK),
    disabledFn: row => !!deleteDisabledReason(row),
    labelFn: row => deleteDisabledReason(row) ?? 'Delete Request',
  });
  return icons;
}

export function vacationRequestsToolbarActions(ctx: VacationRequestsGridContext): ToolbarActionDef[] {
  return ctx.create.visible
    ? [{
        key: CREATE_REQUEST_ACTION, label: 'Create Vacation Request', icon: ICON.add, kind: 'button',
        disabled: !ctx.create.enable, disabledReason: `Create Vacation Request: ${NO_PERMISSION}`,
      }]
    : [];
}

export function vacationRequestsGridConfig(ctx: VacationRequestsGridContext): GridConfig {
  return {
    apiBaseUrl: `${ctx.masterApiUrl}grid/vacation-requests`,
    clientSide: true,
    keyColumn: 'vrfId',
    rowHeight: 26,
    loadingIndicator: 'banner',
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: BACKING_COLUMNS,
    // ID, Reporter and Approved By were not in the column chooser.
    alwaysVisibleColumns: ['vrfId', 'reporter', 'approvedBy'],
    columnLabels: {
      vrfId: 'ID', reporter: 'Reporter', startDate: 'Start Date', endDate: 'End Date', status: 'Status', totalDays: 'Total Days',
      vacation: 'Vacation', floating: 'Floating', sick: 'Sick', other: 'Others', createdDate: 'Submission Date',
      approvedDate: 'Approved Date', approvedBy: 'Approved By',
    },
    // RIMS grid look: headers left; text and dates left; the id centred; day counts right.
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(c === 'vrfId' ? { horizontal: 'center' as const } : DAYS.includes(c) ? { horizontal: 'right' as const } : {}),
    }])),
    columnWidths: {
      vrfId: 120, reporter: 150, startDate: 120, endDate: 120, status: 130, totalDays: 110, vacation: 100, floating: 100, sick: 80,
      other: 90, createdDate: 150, approvedDate: 140, approvedBy: 160,
    },
    stretchColumns: true,
    wrapHeaders: true,
    columnDateFormats: Object.fromEntries(DATES.map(c => [c, 'd MMM, yyyy'])),
    // The request dates are days, not times.
    columnTypes: { startDate: 'date', endDate: 'date' },
    columnColors: { status: (_value, row) => String(row['statusColor'] ?? '') },
    // The ID opens the request.
    hyperlinkColumns: ['vrfId'],
    // The old checkbox (Excel) filters.
    excelFilterColumns: ['reporter', 'status'],
    enableContextMenuFilter: true,
    enableSearch: true,
    // The toolbar's Refresh is the page's own (it reloads the permissions too, as the old one).
    enableRefresh: false,
    enableFitColumns: true,
    enableSavedViews: true,
    enableResetLayout: true,
    // Excel with mainGrid_excelExport, never for a Sales Rep, as before: "Vacation Requests (<date>).xlsx".
    enableExport: ctx.canExport,
    defaultSort: { column: 'vrfId', direction: 'desc' },
    emptyStateMessage: 'No out-of-office requests to show. The list shows your own requests and the ones your role lets you see.',
    persistenceKey: 'rims.out-of-office.requests',
    persistenceVersion: 1,
  };
}

export const REFRESH_ACTION: ToolbarActionDef = {
  key: 'refresh', label: 'Refresh', kind: 'button', position: 'end',
  icon: '<polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />',
};
